import type { FieldId } from "src/types";
import type {
  AiChecklistSuggestion,
  AiSuggestionSelectionMap,
  AiSuggestionsMap,
  AiSuggestionValue,
  AiSuggestionSelectionMatrix,
  AiLinkedFieldSuggestionValue,
  AiRegularSuggestionValue
} from "src/ts-types";

/**
 * Type predicate that determines whether a given value conforms to the
 * `AiLinkedFieldSuggestionValue` structure.
 *
 * A valid linked field suggestion is a non-null object, is not an array, and
 * contains the keys `embeddedFields` and `title`.
 *
 * @param value - The unknown value to validate.
 * @returns True if the value is an `AiLinkedFieldSuggestionValue`; otherwise false.
 */
export function isAiLinkedFieldSuggestionValue(
  value: unknown
): value is AiLinkedFieldSuggestionValue {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "embeddedFields" in value &&
    "title" in value
  );
}

/**
 * Constructs a map of selected checklist field IDs to their corresponding
 * AI checklist suggestions, filtered according to the user's
 * selections.
 *
 * For each `fieldId` in `selectedFields`, this function looks up the user's
 * selection indices and returns an `AiChecklistSuggestion` whose `value`
 * contains only the selected suggestion entries (and selected embedded
 * fields for linked suggestions).
 *
 * @param selectedFields - Field IDs for which the user has selected suggestions.
 * @param selectedSuggestions - Per-field selection state capturing chosen indices.
 * @param allSuggestions - All available AI suggestions keyed by field ID.
 * @returns A map of field ID to filtered `AiChecklistSuggestion` ready to be applied.
 */
export const generateSelectedFieldsMap = (
  selectedFields: Set<FieldId>,
  selectedSuggestions: AiSuggestionSelectionMap,
  allSuggestions: AiSuggestionsMap
) => {
  const selectedFieldsMap = new Map<FieldId, AiChecklistSuggestion>();
  for (const fieldId of selectedFields) {
    if (selectedSuggestions.has(fieldId)) {
      const suggestionArray = selectedSuggestions.get(fieldId) ?? null;
      const suggestions = allSuggestions.get(fieldId) ?? null;
      if (suggestionArray && suggestions) {
        const filteredValue = filterSelectedSuggestionValues(
          suggestions.value,
          suggestionArray
        );
        const selectedAiChecklistSuggestion: AiChecklistSuggestion = {
          ...suggestions,
          value:
            (filteredValue as AiSuggestionValue | null) ?? suggestions.value
        };
        selectedFieldsMap.set(fieldId, selectedAiChecklistSuggestion);
      }
    }
  }
  return selectedFieldsMap;
};

const filterSelectedSuggestionValues = (
  aiSuggestion: AiSuggestionValue,
  selectedSuggestions: AiSuggestionSelectionMatrix
) => {
  if (Array.isArray(aiSuggestion)) {
    let filteredValue: Array<
      AiRegularSuggestionValue | AiLinkedFieldSuggestionValue
    > = [];
    aiSuggestion.forEach(
      (
        value: AiLinkedFieldSuggestionValue | AiRegularSuggestionValue,
        index: number
      ) => {
        if (selectedSuggestions[index] != null) {
          if (isAiLinkedFieldSuggestionValue(value)) {
            filteredValue.push({
              ...value,
              embeddedFields: value.embeddedFields.filter(
                (_, embeddedFieldIndex) =>
                  selectedSuggestions[index]?.has(embeddedFieldIndex)
              )
            });
          } else {
            filteredValue.push(value);
          }
        }
      }
    );
    return filteredValue;
  } else if (selectedSuggestions[0] != null) {
    return aiSuggestion;
  }
  return null;
};
